import React, { useState, useEffect } from 'react';
import Modal from 'react-bootstrap/Modal';
import Button from 'react-bootstrap/Button';
import Spinner from 'react-bootstrap/Spinner';
import Form from 'react-bootstrap/Form';
import InputGroup from 'react-bootstrap/InputGroup';
import { Plus, Trash } from 'react-bootstrap-icons';
import apiClient, { API_ENDPOINTS } from '../api/config';
import { useActiveFeatureFlags } from '../hooks/useActiveFeatureFlags';
import { SYNTHETIC_DATA_MANUAL_ONLY_MESSAGE, TEST_EXECUTION_DISABLED_MESSAGE } from '../constants/featureMessages';

type RunMode = 'smart' | 'fresh' | 'failed_only';

type RunPreview = {
  total: number;
  cached_count: number;
  failed_count: number;
  changed_count: number;
  no_cache: boolean;
};

type DataSourceMode = 'manual' | 'generate_and_run' | 'existing_dataset';

type TestDatasetSummary = {
  id: string;
  name: string;
  row_count: number;
  columns?: string[];
};

type MatchPreview = {
  matched_rows: number;
  total_rows: number;
  missing_test_keys: string[];
  dataset_summaries: Array<{ dataset_id: string; dataset_name: string; row_count: number; matched_rows: number }>;
};

type Props = {
  show: boolean;
  onHide: () => void;
  projectId?: number | null;
  sourceJobId?: number | null;
  artifactId?: number | null;
  testKeys?: string[];
  folderPath?: string[];
  category?: string | null;
  title?: string;
  defaultRunMode?: RunMode;
  onRunStarted?: (run: any) => void;
};

type ArgumentRow = { key: string; value: string };

const BASE_URL_KEY = 'base_url';
const ARGUMENT_REMOVE_BUTTON_WIDTH_PX = 42;

const DEFAULT_ROWS: ArgumentRow[] = [
  { key: 'base_url', value: '' },
  { key: 'email', value: '' },
  { key: 'password', value: '' },
];

const cloneDefaultRows = (): ArgumentRow[] => DEFAULT_ROWS.map(row => ({ ...row }));

const normalizedKey = (key?: string | null): string => (key || '').trim().toLowerCase();

const isBaseUrlKey = (key?: string | null): boolean => normalizedKey(key) === BASE_URL_KEY;

const getBaseUrlRowIndex = (rows: ArgumentRow[]): number => rows.findIndex(row => isBaseUrlKey(row.key));

const ensureBaseUrlRow = (rows: ArgumentRow[]): ArgumentRow[] => {
  if (getBaseUrlRowIndex(rows) >= 0) return rows;
  return [{ key: BASE_URL_KEY, value: '' }, ...rows];
};

const DATA_SOURCE_OPTIONS: Array<{ value: DataSourceMode; label: string; desc: string; badge?: string }> = [
  {
    value: 'manual',
    label: 'Manual inputs',
    desc: 'Use the values entered below for each selected test.',
  },
  {
    value: 'generate_and_run',
    label: 'Generate synthetic variants',
    desc: 'Create fresh rows for the selected tests and run each variant.',
    badge: 'Synthetic',
  },
  {
    value: 'existing_dataset',
    label: 'Use existing dataset',
    desc: 'Run matched rows from saved synthetic datasets.',
  },
];

const ScriptArgumentsDialog: React.FC<Props> = ({
  show,
  onHide,
  projectId,
  sourceJobId,
  artifactId,
  testKeys,
  folderPath,
  category,
  title,
  defaultRunMode,
  onRunStarted,
}) => {

  const {
    flags: featureFlags,
    loading: featureFlagsLoading,
    refresh: refreshFeatureFlags,
  } = useActiveFeatureFlags();
  const testExecutionEnabled = !featureFlagsLoading && featureFlags.test_execution;
  const syntheticDataEnabled = !featureFlagsLoading && featureFlags.synthetic_data;

  const [rows, setRows] = useState<ArgumentRow[]>(cloneDefaultRows);
  const [runName, setRunName] = useState<string>('');
  const [runMode, setRunMode] = useState<RunMode>('smart');
  const [preview, setPreview] = useState<RunPreview | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [dataSource, setDataSource] = useState<DataSourceMode>('manual');
  const [datasets, setDatasets] = useState<TestDatasetSummary[]>([]);
  const [selectedDatasetIds, setSelectedDatasetIds] = useState<string[]>([]);
  const [datasetsLoading, setDatasetsLoading] = useState(false);
  const [matchPreview, setMatchPreview] = useState<MatchPreview | null>(null);
  const [matchLoading, setMatchLoading] = useState(false);
  const [numVariants, setNumVariants] = useState(5);
  const [supportingContext, setSupportingContext] = useState('');
  const [waitingForFeatureFlags, setWaitingForFeatureFlags] = useState(false);

  // Reset and fetch preview whenever the dialog is shown
  useEffect(() => {
    if (!show) return;

    // Organization settings may have been changed since this page mounted.
    // Refresh before allowing a synthetic-data execution choice.
    refreshFeatureFlags();

    let cancelled = false;

    setRows(cloneDefaultRows());
    setRunName('');
    setRunMode(defaultRunMode || 'smart');
    setPreview(null);
    setError(null);
    setBaseUrlError(false);
    setLoading(false);
    setPreviewLoading(false);
    setDataSource('manual');
    setDatasets([]);
    setDatasetsLoading(false);
    setSelectedDatasetIds([]);
    setMatchPreview(null);
    setMatchLoading(false);
    setNumVariants(5);
    setSupportingContext('');
    setWaitingForFeatureFlags(false);

    const loadRunPreview = async () => {
      if (!projectId || !artifactId) {
        setRunMode('fresh');
        return;
      }

      setPreviewLoading(true);
      const params =
        testKeys && testKeys.length > 0
          ? '?' + testKeys.map((testKey) => `test_keys=${encodeURIComponent(testKey)}`).join('&')
          : '';

      try {
        const response = await apiClient.get(API_ENDPOINTS.PLAYWRIGHT_RUN_PREVIEW(projectId, artifactId) + params);
        if (cancelled) return;
        setPreview(response.data);
        if (response.data?.no_cache) {
          setRunMode('fresh');
        }
      } catch {
        if (!cancelled) setPreview(null);
      } finally {
        if (!cancelled) setPreviewLoading(false);
      }
    };

    const loadDatasets = async () => {
      if (!projectId) return;

      setDatasetsLoading(true);
      try {
        const response = await apiClient.get(API_ENDPOINTS.TEST_DATA_LIST(projectId));
        if (!cancelled) setDatasets(Array.isArray(response.data) ? response.data : []);
      } catch {
        if (!cancelled) setDatasets([]);
      } finally {
        if (!cancelled) setDatasetsLoading(false);
      }
    };

    void loadRunPreview();
    void loadDatasets();

    return () => { cancelled = true; };
  }, [show, projectId, artifactId, defaultRunMode, refreshFeatureFlags, testKeys]);

  useEffect(() => {
    if (waitingForFeatureFlags && !featureFlagsLoading) {
      setWaitingForFeatureFlags(false);
      setLoading(false);
    }
  }, [waitingForFeatureFlags, featureFlagsLoading]);

  useEffect(() => {
    if (!show) return;
    setRows(prev => ensureBaseUrlRow(prev));
  }, [show, rows]);

  useEffect(() => {
    if (!show || !projectId || dataSource !== 'existing_dataset' || selectedDatasetIds.length === 0) {
      setMatchPreview(null);
      setMatchLoading(false);
      return;
    }

    let cancelled = false;
    setMatchLoading(true);

    const loadMatchPreview = async () => {
      try {
        const response = await apiClient.post(API_ENDPOINTS.TEST_DATA_MATCH_PREVIEW(projectId), {
          dataset_ids: selectedDatasetIds,
          test_keys: testKeys && testKeys.length > 0 ? testKeys : undefined,
        });
        if (!cancelled) setMatchPreview(response.data);
      } catch {
        if (!cancelled) setMatchPreview(null);
      } finally {
        if (!cancelled) setMatchLoading(false);
      }
    };

    void loadMatchPreview();

    return () => { cancelled = true; };
  }, [show, projectId, dataSource, selectedDatasetIds, testKeys]);

  const addRow = () => setRows(prev => ([...prev, { key: '', value: '' }]));
  const removeRow = (idx: number) => setRows(prev => {
    if (idx === getBaseUrlRowIndex(prev)) return ensureBaseUrlRow(prev);
    return ensureBaseUrlRow(prev.filter((_, i) => i !== idx));
  });
  const updateKey = (idx: number, key: string) => setRows(prev => ensureBaseUrlRow(prev.map((r, i) => {
    if (i !== idx) return r;
    return i === getBaseUrlRowIndex(prev) ? { ...r, key: BASE_URL_KEY } : { ...r, key };
  })));
  const updateValue = (idx: number, value: string) => setRows(prev => prev.map((r, i) => i === idx ? { ...r, value } : r));

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [baseUrlError, setBaseUrlError] = useState(false);
  const [nameError, setNameError] = useState(false);
  const [credKeyErrors, setCredKeyErrors] = useState<Record<number, string>>({});
  const [attachments, setAttachments] = useState<Array<{ file: File; description: string }>>([]);

  const selectDataSource = (mode: DataSourceMode) => {
    if (mode !== 'manual' && !featureFlagsLoading && !featureFlags.synthetic_data) {
      setError(SYNTHETIC_DATA_MANUAL_ONLY_MESSAGE);
      setDataSource('manual');
      return;
    }

    setError(null);
    setDataSource(mode);
  };

  const handleRun = async () => {
    setError(null);
    setBaseUrlError(false);
    setNameError(false);
    setCredKeyErrors({});
    if (!projectId || !artifactId) {
      setError('Missing project/artifact identifiers.');
      return;
    }

    // Wait for the popup-open feature-flag refresh before deciding whether
    // execution or synthetic data is unavailable. This avoids a false
    // "not active" error while the API response is still pending.
    if (featureFlagsLoading) {
      setWaitingForFeatureFlags(true);
      setLoading(true);
      refreshFeatureFlags();
      return;
    }

    if (!testExecutionEnabled) {
      setError(TEST_EXECUTION_DISABLED_MESSAGE);
      return;
    }

    if (dataSource !== 'manual' && !syntheticDataEnabled) {
      setError(SYNTHETIC_DATA_MANUAL_ONLY_MESSAGE);
      return;
    }

    // Run name is required
    if (!runName || runName.trim().length === 0) {
      setNameError(true);
      return;
    }

    // Ensure base_url exists and has a value
    const normalizedRows = ensureBaseUrlRow(rows);
    if (normalizedRows !== rows) setRows(normalizedRows);

    const baseRow = normalizedRows.find(r => isBaseUrlKey(r.key));
    if (!baseRow || !baseRow.value || baseRow.value.trim().length === 0) {
      setBaseUrlError(true);
      return;
    }

    // Reserved keys that are handled directly — skip key-format checks for these
    const RESERVED_KEYS = new Set(['base_url', 'email', 'password']);
    // Extra credential keys (anything beyond base_url/email/password) must use
    // only alphanumeric characters and underscores so they map cleanly to
    // [[TEST_<KEY_UPPERCASE>]] placeholders in generated test steps.
    const keyErrors: Record<number, string> = {};
    const baseUrlRowIndex = getBaseUrlRowIndex(normalizedRows);
    normalizedRows.forEach((r, idx) => {
      const k = r.key?.trim();
      if (!k) return;
      if (isBaseUrlKey(k) && idx !== baseUrlRowIndex) {
        keyErrors[idx] = 'Base URL is already provided above.';
        return;
      }
      if (RESERVED_KEYS.has(k.toLowerCase())) return;
      if (!/^[a-zA-Z0-9_]+$/.test(k)) {
        keyErrors[idx] = 'Use only letters, numbers, and underscores (e.g. first_name)';
      }
    });
    if (Object.keys(keyErrors).length > 0) {
      setCredKeyErrors(keyErrors);
      return;
    }

    const payload: Record<string, any> = {
      project_id: projectId,
      artifact_id: artifactId,
      run_mode: runMode,
    };
    if (sourceJobId) {
      payload.source_job_id = sourceJobId;
    }
    if (runName && runName.trim().length > 0) {
      payload.run_name = runName.trim();
    }
    if (testKeys && testKeys.length > 0) {
      payload.test_keys = testKeys;
    }
    if (folderPath && folderPath.length > 0) {
      payload.folder_path = folderPath;
    }
    if (category) {
      payload.category = category;
    }
    // merge key/value rows — base_url/email/password are top-level fields;
    // everything else goes into the credentials dict so the runner can resolve
    // [[TEST_<KEY_UPPERCASE>]] placeholders in generated test steps.
    const TOP_LEVEL_KEYS = new Set(['base_url', 'email', 'password']);
    const credentials: Record<string, string> = {};
    normalizedRows.forEach(r => {
      if (!r.key || r.key.trim().length === 0) return;
      const k = r.key.trim();
      if (TOP_LEVEL_KEYS.has(k.toLowerCase())) {
        payload[k] = r.value;
      } else {
        credentials[k] = r.value;
      }
    });
    if (Object.keys(credentials).length > 0) {
      payload.credentials = credentials;
    }

    if (dataSource === 'manual' && !sourceJobId) {
      payload.synthetic_data = {
        mode: 'manual',
      };
    } else if (dataSource === 'existing_dataset') {
      if (selectedDatasetIds.length === 0) {
        setError('Select at least one synthetic dataset.');
        return;
      }
      payload.synthetic_data = {
        mode: 'existing_dataset',
        dataset_ids: selectedDatasetIds,
        unmatched_behavior: 'fallback_manual',
      };
    } else if (dataSource === 'generate_and_run') {
      payload.synthetic_data = {
        mode: 'generate_and_run',
        num_variants: Math.max(1, Math.min(50, Number(numVariants) || 1)),
        supporting_context: supportingContext.trim() || undefined,
        unmatched_behavior: 'fallback_manual',
        persist_dataset: true,
      };
    }

    const emptyAttachment = attachments.find((attachment) => attachment.file && attachment.file.size === 0);
    if (emptyAttachment?.file) {
      setError(`"${emptyAttachment.file.name}" is empty and cannot be uploaded.`);
      return;
    }

    try {
      setLoading(true);
      let response;
      if (attachments.length > 0) {
        const form = new FormData();
        form.append('body', JSON.stringify(payload));
        attachments.forEach(a => {
          form.append('upload_files', a.file);
          form.append('upload_descriptions', a.description);
        });
        response = await apiClient.post(
          API_ENDPOINTS.PLAYWRIGHT_CREATE_JOB_WITH_FILES(projectId),
          form,
          { headers: { 'Content-Type': 'multipart/form-data' } },
        );
      } else {
        response = await apiClient.post(API_ENDPOINTS.PLAYWRIGHT_CREATE_JOB(projectId), payload);
      }
      const run = response.data;
      // notify parent that a run was started so it can track status
      if (onRunStarted)
        onRunStarted(run);
      // Close dialog on success
      onHide();
    } catch (err: any) {
      const raw = err?.response?.data?.detail;
      const message = Array.isArray(raw)
        ? raw.map((e: any) => e?.msg || JSON.stringify(e)).join('; ')
        : (typeof raw === 'string' ? raw : null) || err?.message || 'Failed to start playwright run';
      setError(message);
    } finally {
      setLoading(false);
    }
  };

  const hasCache = preview && !preview.no_cache;

  return (
    <Modal show={show} onHide={onHide} size="lg" centered>
      <Modal.Header closeButton>
        <Modal.Title>{title || 'Run Playwright Scripts'}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="d-flex flex-column gap-2">
          {error && <div className="text-danger mb-2">{error}</div>}
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold mb-1">
              Run Name <span className="text-danger">*</span>
            </Form.Label>
            <Form.Control
              type="text"
              placeholder="e.g. Sprint 3 smoke test"
              value={runName}
              isInvalid={nameError}
              onChange={(e) => { setRunName(e.target.value); if (e.target.value.trim()) setNameError(false); }}
            />
            <Form.Control.Feedback type="invalid">Run name is required.</Form.Control.Feedback>
          </Form.Group>

          {/* ---- Run Mode Selector — only shown for subsequent runs (cache exists) ---- */}
          {!previewLoading && hasCache && (
          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold mb-1">Run Mode</Form.Label>
              <div className="d-flex flex-column gap-1">
                {(() => {
                  const scopeTotal = (testKeys && testKeys.length > 0) ? testKeys.length : preview!.total;
                  return ([
                  { value: 'smart', label: 'Smart Run', desc: hasCache ? `Re-resolve ${preview!.failed_count + preview!.changed_count} failed/changed. Skip AI for ${Math.max(0, scopeTotal - preview!.failed_count - preview!.changed_count)} cached.` : 'Reuse cached AI actions where possible.' },
                  { value: 'failed_only', label: 'Run Failed Only', desc: hasCache ? `Only re-run the ${preview!.failed_count} test${preview!.failed_count !== 1 ? 's' : ''} that failed last time.` : 'Only re-run tests that failed last time.' },
                  { value: 'fresh', label: 'Run Everything Fresh', desc: hasCache ? `Re-run all ${scopeTotal} tests. Re-resolve every AI action from scratch.` : 'Re-run all tests and re-resolve every AI action.' },
                ] as { value: RunMode; label: string; desc: string }[]);
                })().map((opt) => (
                  <div
                    key={opt.value}
                    onClick={() => setRunMode(opt.value)}
                    style={{
                      border: `1.5px solid ${runMode === opt.value ? '#6366f1' : '#dee2e6'}`,
                      borderRadius: 8,
                      padding: '8px 12px',
                      cursor: 'pointer',
                      background: runMode === opt.value ? '#f5f3ff' : 'white',
                      transition: 'border-color 0.15s, background 0.15s',
                    }}
                  >
                    <div className="d-flex align-items-center gap-2">
                      <Form.Check
                        type="radio"
                        checked={runMode === opt.value}
                        onChange={() => setRunMode(opt.value)}
                        id={`run-mode-${opt.value}`}
                        label=""
                        style={{ margin: 0 }}
                      />
                      <span className="small fw-semibold">{opt.label}</span>
                      {opt.value === 'smart' && <span className="badge ms-1" style={{ background: '#6366f1', color: 'white', fontSize: 10 }}>Recommended</span>}
                    </div>
                    <div className="small text-muted ms-4" style={{ marginTop: 2 }}>{opt.desc}</div>
                  </div>
                ))}
              </div>
          </Form.Group>
          )}

          <Form.Group className="mb-2">
            <Form.Label className="small fw-semibold mb-1">Data Source</Form.Label>
            <div className="d-flex flex-column flex-md-row gap-2">
              {DATA_SOURCE_OPTIONS.map((opt) => (
                <div
                  key={opt.value}
                  role="button"
                  tabIndex={0}
                  onClick={() => selectDataSource(opt.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault();
                      selectDataSource(opt.value);
                    }
                  }}
                  style={{
                    flex: 1,
                    border: `1.5px solid ${dataSource === opt.value ? '#6366f1' : '#dee2e6'}`,
                    borderRadius: 8,
                    padding: '8px 10px',
                    cursor: 'pointer',
                    background: dataSource === opt.value ? '#f5f3ff' : 'white',
                    transition: 'border-color 0.15s, background 0.15s',
                  }}
                >
                  <div className="d-flex align-items-center gap-2">
                    <Form.Check
                      type="radio"
                      checked={dataSource === opt.value}
                      onChange={() => selectDataSource(opt.value)}
                      id={`data-source-${opt.value}`}
                      label=""
                      style={{ margin: 0 }}
                    />
                    <span className="small fw-semibold">{opt.label}</span>
                    {opt.badge && <span className="badge ms-1" style={{ background: '#0e7490', color: 'white', fontSize: 10 }}>{opt.badge}</span>}
                  </div>
                  <div className="small text-muted" style={{ marginLeft: 24, marginTop: 2, lineHeight: 1.35 }}>{opt.desc}</div>
                </div>
              ))}
            </div>
          </Form.Group>

          {dataSource === 'generate_and_run' && (
            <div className="border rounded p-2 mb-2" style={{ background: '#fbfcfe' }}>
              <Form.Group className="mb-2">
                <Form.Label className="small fw-semibold mb-1">Variants Per Test</Form.Label>
                <Form.Control
                  size="sm"
                  type="number"
                  min={1}
                  max={50}
                  value={numVariants}
                  onChange={(e) => setNumVariants(Number(e.target.value))}
                />
              </Form.Group>
              <Form.Group>
                <Form.Label className="small fw-semibold mb-1">Supporting Context</Form.Label>
                <Form.Control
                  as="textarea"
                  rows={2}
                  value={supportingContext}
                  onChange={(e) => setSupportingContext(e.target.value)}
                />
              </Form.Group>
            </div>
          )}

          {dataSource === 'existing_dataset' && (
            <div className="border rounded p-2 mb-2" style={{ background: '#fbfcfe' }}>
              {datasetsLoading ? (
                <div className="d-flex align-items-center gap-2 small text-muted">
                  <Spinner animation="border" size="sm" /> Loading datasets
                </div>
              ) : datasets.length === 0 ? (
                <div className="small text-muted">No synthetic datasets found.</div>
              ) : (
                <div className="d-flex flex-column gap-2">
                  {datasets.map((dataset) => (
                    <Form.Check
                      key={dataset.id}
                      type="checkbox"
                      id={`dataset-${dataset.id}`}
                      checked={selectedDatasetIds.includes(dataset.id)}
                      onChange={(e) => {
                        setSelectedDatasetIds((prev) => e.target.checked
                          ? [...prev, dataset.id]
                          : prev.filter((id) => id !== dataset.id));
                      }}
                      label={
                        <span className="small">
                          <span className="fw-semibold">{dataset.name}</span>
                          <span className="text-muted ms-2">{dataset.row_count} rows</span>
                        </span>
                      }
                    />
                  ))}
                </div>
              )}
              {selectedDatasetIds.length > 0 && (
                <div className="small text-muted mt-2">
                  {matchLoading ? (
                    <span className="d-inline-flex align-items-center gap-2"><Spinner animation="border" size="sm" /> Matching rows</span>
                  ) : matchPreview ? (
                    <span>
                      {matchPreview.matched_rows} of {matchPreview.total_rows} rows match{matchPreview.missing_test_keys.length > 0 ? `; ${matchPreview.missing_test_keys.length} tests will use manual inputs` : ''}.
                    </span>
                  ) : null}
                </div>
              )}
            </div>
          )}

          {rows.map((row, idx) => {
            const isBaseUrlRow = idx === getBaseUrlRowIndex(rows);
            const credKeyError = credKeyErrors[idx];
            return (
              <InputGroup key={idx} className="mb-2">
                <Form.Control
                  placeholder="Key"
                  value={row.key}
                  readOnly={isBaseUrlRow}
                  isInvalid={!!credKeyError}
                  style={isBaseUrlRow
                    ? { flex: `0 0 calc((100% - ${ARGUMENT_REMOVE_BUTTON_WIDTH_PX}px) / 2)` }
                    : undefined}
                  onChange={(e) => {
                    const newKey = e.target.value;
                    updateKey(idx, newKey);
                    if (credKeyErrors[idx]) {
                      setCredKeyErrors(prev => { const n = { ...prev }; delete n[idx]; return n; });
                    }
                  }}
                />
                <Form.Control
                  placeholder="Value"
                  value={row.value}
                  isInvalid={isBaseUrlRow && baseUrlError}
                  onChange={(e) => {
                    updateValue(idx, e.target.value);
                    if (isBaseUrlRow && e.target.value && e.target.value.trim().length > 0)
                      setBaseUrlError(false);
                  }}
                />
                {!isBaseUrlRow && (
                  <Button
                    variant="outline-danger"
                    onClick={() => removeRow(idx)}
                    aria-label="Remove row"
                    style={{ flex: `0 0 ${ARGUMENT_REMOVE_BUTTON_WIDTH_PX}px`, width: ARGUMENT_REMOVE_BUTTON_WIDTH_PX }}
                  >
                    <Trash />
                  </Button>
                )}
                {isBaseUrlRow && baseUrlError && (
                  <Form.Control.Feedback type="invalid">Base URL is required.</Form.Control.Feedback>
                )}
                {credKeyError && (
                  <Form.Control.Feedback type="invalid">{credKeyError}</Form.Control.Feedback>
                )}
              </InputGroup>
            );
          })}

          <div className="d-flex justify-content-start">
            <Button variant="outline-primary" size="sm" onClick={addRow}>
              <Plus />
            </Button>
          </div>

          {/* ---- File Attachments ---- */}
          <div className="mt-3">
            <div className="d-flex align-items-center justify-content-between mb-1">
              <span className="small fw-semibold">File Attachments <span className="text-muted fw-normal">(optional)</span></span>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => setAttachments(prev => [...prev, { file: null as any, description: '' }])}
              >
                <Plus /> Add File
              </Button>
            </div>
            {attachments.map((att, idx) => (
              <InputGroup key={idx} className="mb-2">
                <Form.Control
                  type="file"
                  size="sm"
                  onChange={(e) => {
                    const file = (e.target as HTMLInputElement).files?.[0];
                    if (!file) return;
                    if (file.size === 0) {
                      setError(`"${file.name}" is empty and cannot be uploaded.`);
                      (e.target as HTMLInputElement).value = '';
                      return;
                    }
                    setError(null);
                    setAttachments(prev => prev.map((a, i) => i === idx ? { ...a, file } : a));
                  }}
                />
                <Form.Control
                  size="sm"
                  placeholder="Description — match wording in test step, e.g. 'passport photo', 'invoice PDF'"
                  value={att.description}
                  onChange={(e) => setAttachments(prev => prev.map((a, i) => i === idx ? { ...a, description: e.target.value } : a))}
                />
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => setAttachments(prev => prev.filter((_, i) => i !== idx))}
                  aria-label="Remove attachment"
                >
                  <Trash />
                </Button>
              </InputGroup>
            ))}
            {attachments.length > 0 && (
              <div className="small text-muted mt-1" style={{ lineHeight: 1.4 }}>
                <strong>Tip:</strong> The description is matched against the wording in each test step that triggers an upload.
                Use the same keywords your test steps use, e.g. if a step says <em>"Upload the invoice PDF"</em>, describe that file as <em>"invoice PDF"</em>.
              </div>
            )}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <Button variant="primary" size="sm" onClick={handleRun} disabled={loading || !runName.trim() || !testExecutionEnabled}>
          {loading ? <Spinner animation="border" size="sm" /> : 'Run'}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default ScriptArgumentsDialog;
